import React, { FC, ReactElement, useState, useEffect } from 'react';
import classNames from 'classnames/bind';
import { sendBenefitsLeadForm } from 'api/requests';
import { Input } from 'ui/CustomInput';
import Phone from 'ui/CustomPhone';
import Modal from 'ui/Modal';
import reCaptcha from 'utils/reCaptcha';
import setConnector from 'utils/hidden';
import {
  firstNameValidator,
  lastNameValidator,
  emailValidator,
  emptyValidator,
  numberValidator,
  emailBlacklistValidator,
} from 'utils/validator';
import { GOOGLE_KEY } from 'utils/constants';
import { processPhoneNumber } from 'utils/helper';
import { FormData } from 'sections/Benefits/components/LeadForm/LeadForm.types';
import { EForm } from 'sections/Benefits/components/LeadForm/LeadForm.data';
import Telegram from 'error-handler/helper/Telegram';
import styles from 'sections/Benefits/components/LeadForm/LeadForm.module.scss';

const cn = classNames.bind(styles);

interface LeadForm {
  pageTitle: string;
  formUrl: string;
  leadSubtype: string;
  heading?: string;
  buttonText?: string;
  formClassNames?: {
    container?: string;
    form?: string;
    formElements?: string;
    heading?: string;
    buttons?: string;
    button?: string;
    preloader?: string;
  };
  inputClassNames?: {
    container?: string;
    label?: string;
    labelText?: string;
    input?: string;
    error?: string;
  };
  selectClassNames?: {
    selectPanel?: string;
    selectPanelPhone?: string;
    selectPanelNumber?: string;
    selectInput?: string;
    inputLabel?: string;
    input?: string;
  },
}

const LeadForm: FC<LeadForm> = ({
  pageTitle,
  formUrl,
  leadSubtype,
  buttonText = 'Get Started Now',
  heading,
  formClassNames,
  inputClassNames,
  selectClassNames,
}): ReactElement => {
  const [mask, setMask] = useState<string>('');
  const [loaderForm, setLoaderForm] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    number: {
      iso: '',
      phone: '',
    },
    email: '',
    website: '',
    'g-recaptcha-response': '',
  });

  useEffect(() => {
    reCaptcha();
  }, []);

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    number: '',
    email: '',
    website: '',
  });

  const validator = (): boolean => {
    const firstNameValidatorBool: boolean = firstNameValidator({ formData, setErrors });
    const lastNameValidatorBool: boolean = lastNameValidator({ formData, setErrors });
    const numberValidatorBool: boolean = numberValidator({ formData, setErrors, mask });
    const emailValidatorBool: boolean = emailValidator({ formData, setErrors });
    const blackListValidator: boolean = emailBlacklistValidator({ formData, setErrors });
    const website: boolean = emptyValidator({ formData, setErrors, key: 'website', minChar: 0 });
    return firstNameValidatorBool && lastNameValidatorBool && emailValidatorBool && website && numberValidatorBool && blackListValidator;
  };

  const submitHandler = async (e: React.MouseEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      setLoaderForm(true);
      if (!validator()) return;

      let reCaptchaToken: string = '';

      await grecaptcha.execute(GOOGLE_KEY, { action: 'submit' }).then((token: string) => {
        reCaptchaToken = token;
      });

      const ApiData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: processPhoneNumber(formData.number.iso + formData.number.phone),
        email: formData.email,
        website: formData.website,
        form_url: formUrl,
        page_title: pageTitle,
        lead_subtype: leadSubtype,
        'g-recaptcha-response': reCaptchaToken,
        ...setConnector(),
      };

      await sendBenefitsLeadForm(ApiData);

      await new Telegram('cargoes').sendObj(
        [
          {
            label: 'First Name: ',
            value: ApiData.first_name,
          },
          {
            label: 'Last Name: ',
            value: ApiData.last_name,
          },
          {
            label: 'Phone: ',
            value: ApiData.phone,
          },
          {
            label: 'Email: ',
            value: ApiData.email,
          },
          {
            label: 'Website: ',
            value: ApiData.website,
          },
        ],
        pageTitle,
      );

      setModalVisible(true);
    } catch (error: any) {
      return alert(error.message);
    } finally {
      setLoaderForm(false);
    }
  };

  const modalHandler = () => (location.href = location.origin);

  return (
    <div className={cn('lead-form', formClassNames?.container)}>
      <form
        className={cn('lead-form__form', formClassNames?.form)}
        onSubmit={(e: React.MouseEvent<HTMLFormElement>) => submitHandler(e)}
      >

        {heading ? <h3 className={cn('lead-form__heading', formClassNames?.heading)}>{heading}</h3> : null}

        <div className={cn('lead-form__form-elements', formClassNames?.formElements)}>
          <Modal
            title="Your message has been successfully sent."
            text="We will contact you very soon!"
            btnText="Main page"
            modalHandler={modalHandler}
            open={modalVisible}
            setModalVisible={setModalVisible}
          />

          <Input
            id="first-name"
            label="First Name"
            name={EForm.firstName}
            placeholder="Darlene"
            value={formData.firstName ?? ''}
            setFormData={setFormData}
            setFormErrors={setErrors}
            error={errors.firstName}
            classNames={inputClassNames}
          />

          <Input
            id="last-name"
            label="Last Name"
            name={EForm.lastName}
            placeholder="Robertson"
            value={formData.lastName ?? ''}
            setFormData={setFormData}
            setFormErrors={setErrors}
            error={errors.lastName}
            classNames={inputClassNames}
          />

          <Phone
            label="Phone number"
            error={errors.number}
            name={EForm.number}
            value={''}
            setFormData={setFormData}
            setErrors={setErrors}
            callback={(mask: any) => setMask(mask)}
            selectClassNames={selectClassNames}
          />

          <Input
            id="email"
            label="E-Business Email Address"
            name={EForm.email}
            placeholder="darlenerobertson@mail.com"
            value={formData.email ?? ''}
            setFormData={setFormData}
            setFormErrors={setErrors}
            error={errors.email}
            classNames={inputClassNames}
          />

          <Input
            id="website"
            label="Website"
            name={EForm.website}
            placeholder="www.robertson.com"
            value={formData.website ?? ''}
            setFormData={setFormData}
            setFormErrors={setErrors}
            error={errors.website}
            classNames={inputClassNames}
          />
        </div>


        <div className={cn('lead-form__buttons', formClassNames?.buttons)}>
          <button className={cn('lead-form__button-submit', formClassNames?.button)} type="submit">
            {loaderForm ? <div className={cn('lead-form__preloader', formClassNames?.preloader)}></div> : ''}
            {buttonText}
          </button>
        </div>
      </form>
    </div>
  );
};

export { LeadForm };
